import React, { useState, useEffect } from 'react';
import { Search, Check, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

export default function JefaturaPrestamos() {
    const [prestamos, setPrestamos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [gestion, setGestion] = useState(new Date().getFullYear());
    const [observaciones, setObservaciones] = useState({});
    const [showModal, setShowModal] = useState(false);
    const [modalAction, setModalAction] = useState(''); // 'aprobado' o 'observado'
    const [selectedPrestamo, setSelectedPrestamo] = useState(null);
    const [modalObservacion, setModalObservacion] = useState('');

    useEffect(() => {
        loadPrestamos();
    }, [gestion]);

    const loadPrestamos = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_URL}/jefatura/prestamos?gestion=${gestion}`);
            const data = await response.json();
            setPrestamos(data.prestamos || []);
            // Inicializar observaciones locales
            const obs = {};
            (data.prestamos || []).forEach(p => {
                obs[p.id_prestamo] = p.observaciones_jefatura || '';
            });
            setObservaciones(obs);
        } catch (error) {
            console.error('Error:', error);
            setPrestamos([]);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (prestamo, action) => {
        setSelectedPrestamo(prestamo);
        setModalAction(action);
        setModalObservacion('');
        setShowModal(true);
    };

    const handleConfirm = async () => {
        if (!selectedPrestamo) return;
        try {
            await fetch(`${API_URL}/jefatura/prestamos/${selectedPrestamo.id_prestamo}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    estado: modalAction,
                    observaciones_jefatura: modalObservacion || null
                })
            });
            setShowModal(false);
            setSelectedPrestamo(null);
            setModalObservacion('');
            loadPrestamos();
        } catch (error) {
            console.error('Error:', error);
        }
    };

    const handleUpdateDevolucion = async (id, devolucion) => {
        try {
            await fetch(`${API_URL}/jefatura/prestamos/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ devolucion: devolucion === 'SI' })
            });
            loadPrestamos();
        } catch (error) {
            console.error('Error:', error);
        }
    };

    const handleObservacionChange = (id, value) => {
        setObservaciones(prev => ({ ...prev, [id]: value }));
    };

    const handleSaveObservacion = async (id) => {
        try {
            await fetch(`${API_URL}/jefatura/prestamos/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ observaciones_jefatura: observaciones[id] })
            });
            loadPrestamos();
        } catch (error) {
            console.error('Error:', error);
        }
    };

    const getEstadoLabel = (estado) => {
        switch (estado) {
            case 'pendiente': return 'Pendiente';
            case 'aprobado': return 'Aprobado';
            case 'observado': return 'Observado';
            default: return 'Pendiente';
        }
    };

    const getEstadoColor = (estado) => {
        switch (estado) {
            case 'pendiente': return 'bg-gray-200 text-gray-700';
            case 'aprobado': return 'bg-green-200 text-green-800';
            case 'observado': return 'bg-red-200 text-red-800';
            default: return 'bg-gray-200 text-gray-700';
        }
    };

    const getModalTitle = () => {
        return modalAction === 'aprobado' ? 'Aprobar Préstamo' : 'Rechazar Préstamo';
    };

    const getModalMessage = () => {
        return modalAction === 'aprobado'
            ? 'Introduzca un comentario para la aprobación del préstamo (opcional).'
            : 'Introduzca el motivo del rechazo de la solicitud de préstamo.';
    };

    const filteredPrestamos = prestamos.filter(p =>
        searchTerm === '' ||
        p.articulo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.sala?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) return <div className="p-6 text-center">Cargando...</div>;

    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-xl font-bold text-gray-800">Jefatura de Carrera &gt; Préstamos</h1>
            </div>

            <div className="mb-6 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                <div className="flex flex-wrap gap-4 items-end">

                    <div className="relative">
                        <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">GESTIÓN</label>
                        <select
                            value={gestion}
                    onChange={(e) => setGestion(parseInt(e.target.value))}
                            className="px-4 py-2 border border-gray-300 rounded-full bg-white text-sm appearance-none pr-8 hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 min-w-[100px]"
                        >
                            {[2024, 2025, 2026, 2027].map(g => (
                        <option key={g} value={g}>Gestión {g}</option>
                    ))}
                        </select>
                        <span className="absolute right-3 top-[30px] pointer-events-none text-gray-400 text-xs">▼</span>
                    </div>

                    <div className="flex-grow flex items-end gap-2">
                        <div className="relative flex-grow">
                            <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">BUSCAR</label>
                            <div className="relative">
                                <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
                                <input
                                    type="text"
                                    placeholder="Buscar..."
                                    value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 hover:border-blue-500"
                                />
                            </div>
                        </div>
                        {/* <button onClick={exportToExcel} className="p-2 bg-green-600 text-white rounded-full hover:bg-green-700 transition shadow-sm" title="Descargar Excel">
                            <Download size={20} />
                        </button> */}
                    </div>
                </div>
            </div>

            {/* Filtros */}
            {/* <div className="mb-4 flex flex-wrap gap-3">
                <select
                    value={gestion}
                    onChange={(e) => setGestion(parseInt(e.target.value))}
                    className="px-4 py-2 border border-gray-300 rounded-full bg-white text-sm"
                >
                    {[2024, 2025, 2026, 2027].map(g => (
                        <option key={g} value={g}>Gestión {g}</option>
                    ))}
                </select>
                <div className="flex-1 max-w-md relative">
                    <input type="text" placeholder="Buscar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-full" />
                    <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                </div>
            </div> */}

            {/* Tabla de préstamos */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="px-3 py-3 text-center font-semibold border">No</th>
                                <th className="px-3 py-3 text-center font-semibold border">Docente</th>
                                <th className="px-3 py-3 text-center font-semibold border">Artículo</th>
                                <th className="px-3 py-3 text-center font-semibold border">Cantidad</th>
                                <th className="px-3 py-3 text-center font-semibold border">Sala</th>
                                <th className="px-3 py-3 text-center font-semibold border">Comentarios</th>
                                <th className="px-3 py-3 text-center font-semibold border">Fecha Solicitud</th>
                                <th className="px-3 py-3 text-center font-semibold border">Hora Solicitud</th>
                                <th className="px-3 py-3 text-center font-semibold border">Fecha Devolución</th>
                                <th className="px-3 py-3 text-center font-semibold border">Hora Devolución</th>
                                <th className="px-3 py-3 text-center font-semibold border">Acciones</th>
                                <th className="px-3 py-3 text-center font-semibold border">Estado</th>
                                <th className="px-3 py-3 text-center font-semibold border">Observaciones Jefatura de Carrera</th>
                                <th className="px-3 py-3 text-center font-semibold border">Devolución</th>
                            </tr>
                        </thead>
                        <tbody >
                            {filteredPrestamos.length === 0 ? (
                                <tr><td colSpan="13" className="px-4 py-8 text-center text-gray-500">No hay préstamos registrados</td></tr>
                            ) : (
                                filteredPrestamos.map((p, index) => (
                                    <tr key={p.id_prestamo} className="hover:bg-gray-50">
                                        <td className="px-3 py-2 text-center border">{index + 1}</td>
                                        <td className="px-3 py-2 text-center border font-medium" >{p.docente_nombre || '-'}</td>
                                        <td className="px-3 py-2 border">{p.articulo || '-'}</td>
                                        <td className="px-3 py-2 border text-center">{p.cantidad || 1}</td>
                                        <td className="px-3 py-2 border">{p.sala || '-'}</td>
                                        <td className="px-3 py-2 border">{p.comentarios || '-'}</td>
                                        <td className="px-3 py-2 border text-center">
                                            {p.fecha_solicitud ? new Date(p.fecha_solicitud).toLocaleDateString() : '-'}
                                        </td>
                                        <td className="px-3 py-2 border text-center">
                                            {p.hora_solicitud || '-'}
                                        </td>
                                        <td className="px-3 py-2 border text-center">
                                            {p.fecha_devolucion ? new Date(p.fecha_devolucion).toLocaleDateString() : '-'}
                                        </td>
                                        <td className="px-3 py-2 border text-center">
                                            {p.hora_devolucion || '-'}
                                        </td>
                                        {/* Acciones - Siempre verde y rojo */}
                                        <td className="px-3 py-2 border">
                                            <div className="flex justify-center gap-1">
                                                <button
                                                    onClick={() => handleOpenModal(p, 'aprobado')}
                                                    className="p-2 rounded-full bg-green-500 text-white hover:bg-green-600 transition-colors"
                                                    title="Aprobar"
                                                >
                                                    <Check size={16} />
                                                </button>
                                                <button
                                                    onClick={() => handleOpenModal(p, 'observado')}
                                                    className="p-2 rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors"
                                                    title="Observar/Rechazar"
                                                >
                                                    <X size={16} />
                                                </button>
                                            </div>
                                        </td>
                                        {/* Estado */}
                                        <td className="px-3 py-2 border text-center">
                                            <span className={`px-2 py-1 rounded-full text-xs whitespace-nowrap ${getEstadoColor(p.estado)}`}>
                                                {getEstadoLabel(p.estado)}
                                            </span>
                                        </td>
                                        {/* Observaciones Jefatura */}
                                        <td className="px-3 py-2 border">
                                            <span className="text-xs">{p.observaciones_jefatura || '-'}</span>
                                        </td>
                                        {/* Devolución */}
                                        <td className="px-3 py-2 border text-center">
                                            <select
                                                value={p.devuelto ? 'SI' : 'NO'}
                                                onChange={(e) => handleUpdateDevolucion(p.id_prestamo, e.target.value)}
                                                className="px-2 py-1 border border-gray-300 rounded text-xs"
                                            >
                                                <option value="NO">NO</option>
                                                <option value="SI">SI</option>
                                            </select>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal para Aprobar/Rechazar */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                        <div className="flex justify-between items-center px-6 py-4 border-b">
                            <h2 className="text-xl font-bold text-blue-900">{getModalTitle()}</h2>
                            <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                                <X size={24} />
                            </button>
                        </div>
                        <div className="p-6">
                            <p className="text-sm text-gray-700 mb-4">
                                {getModalMessage()}
                            </p>
                            <textarea
                                value={modalObservacion}
                                onChange={(e) => setModalObservacion(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                rows={4}
                                placeholder="Escriba aquí..."
                            />
                        </div>
                        <div className="flex justify-end px-6 py-4 border-t">
                            <button
                                onClick={handleConfirm}
                                className="px-6 py-2 bg-blue-900 text-white rounded-md hover:bg-blue-800"
                            >
                                Guardar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
